"use client"
import React from 'react'
import { createPortal } from 'react-dom'
import { AnimatePresence, motion } from 'framer-motion'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { lockScroll, unlockScroll } from '@/shared/ui/scrollLock'

type RightDrawerProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  title?: string
  header?: React.ReactNode
  headerRight?: React.ReactNode
  className?: string
  variant?: 'default' | 'high'
  overlayClassName?: string
  overlayContent?: React.ReactNode
  children: React.ReactNode
}

export function RightDrawer({
  open,
  onOpenChange,
  title,
  header,
  headerRight,
  className,
  children,
  variant = 'default',
  overlayClassName,
  overlayContent,
}: RightDrawerProps) {
  const [portalElement, setPortalElement] = React.useState<HTMLElement | null>(null)

  React.useEffect(() => {
    setPortalElement(document.body)
  }, [])

  React.useEffect(() => {
    if (!open) return
    lockScroll()
    return () => {
      unlockScroll()
    }
  }, [open])

  if (!portalElement) return null

  const overlayZ = variant === 'high' ? 'z-[100000]' : 'z-[120]'
  const panelZ = variant === 'high' ? 'z-[100001]' : 'z-[130]'

  return createPortal(
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className={cn('fixed inset-0 bg-black/40', overlayZ, overlayClassName)}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => onOpenChange(false)}
          >
            {overlayContent}
          </motion.div>
          <motion.aside
            className={cn(
              'fixed right-0 top-0 flex h-full w-[85%] max-w-[380px] flex-col border-l border-border bg-background shadow-xl',
              panelZ,
              className,
            )}
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ duration: 0.25, ease: 'easeInOut' }}
            role="dialog"
            aria-modal="true"
           >
            <div className="flex items-center justify-between border-b border-border px-4 py-3">
              <div className="flex-1 min-w-0 pr-2 text-base font-medium truncate">
                {header ?? title}
              </div>
              <div className="flex items-center gap-2">
                {headerRight}
                <button
                  aria-label="Fechar"
                  onClick={() => onOpenChange(false)}
                  className="rounded p-1 hover:bg-muted"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto px-4 py-4">
              {children}
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>,
    portalElement,
  )
}
